<template>
  <div
    v-if="$can('read', 'LineLiff')"
    style="height: inherit"
  >
    <b-overlay
      :show="showOverlay"
      rounded="sm"
    >
      <!-- Table Container Card -->
      <b-card
        no-body
      >

        <div class="m-2">

          <!-- Table Top -->
          <b-row>

            <!-- Per Page -->
            <b-col
              cols="12"
              sm="12"
              md="6"
            >
              <b-row>
                <b-col
                  cols="12"
                  sm="12"
                  md="3"
                  class="d-flex align-items-center justify-content-start mb-1 mb-md-0 pr-0"
                >
                  <label>{{ $t('Entries') }}</label>
                  <v-select
                    v-model="perPage"
                    :dir="$store.state.appConfig.isRTL ? 'rtl' : 'ltr'"
                    :options="perPageOptions"
                    :clearable="false"
                    class="per-page-selector d-inline-block ml-50 mr-1 w-100"
                  />
                </b-col>
                <b-col
                  cols="12"
                  sm="12"
                  md="9"
                  class="d-flex align-items-center justify-content-start mb-1 mb-md-0 pl-md-0 pr-md-0"
                >
                  <div class="d-flex align-items-center justify-content-end w-100">
                    <b-form-input
                      v-model="searchQuery"
                      class="d-inline-block mr-1"
                      :placeholder="`${$t('Search')}...`"
                    />
                    <b-button
                      v-if="$can('create', 'LineLiff')"
                      variant="primary"
                      style="width: 100%; max-width: 180px;"
                      @click="addLineLiffForm"
                    >
                      {{ `${$t('Add')} ${$t('LineLiff')}` }}
                    </b-button>
                  </div>
                </b-col>
              </b-row>
            </b-col>
            <!-- Pagination -->
            <b-col
              cols="12"
              sm="12"
              md="6"
              class="d-flex align-items-center justify-content-center justify-content-sm-end"
            >
              <span class="text-muted mr-2">
                {{ $t('Showing {from} to {to} of {of} entries',
                      {from: dataMeta.from, to: dataMeta.to, of: dataMeta.of })
                }}
              </span>
              <b-pagination
                v-model="currentPage"
                :total-rows="totals"
                :per-page="perPage"
                first-number
                last-number
                class="mb-0 mt-1 mt-sm-0"
                prev-class="prev-item"
                next-class="next-item"
              >
                <template #prev-text>
                  <feather-icon
                    icon="ChevronLeftIcon"
                    size="18"
                  />
                </template>
                <template #next-text>
                  <feather-icon
                    icon="ChevronRightIcon"
                    size="18"
                  />
                </template>
              </b-pagination>

            </b-col>

          </b-row>

        </div>
        <b-table
          ref="refTable"
          :items="getList"
          responsive
          :fields="tableColumns"
          primary-key="id"
          :sort-by.sync="sortBy"
          show-empty
          hover
          :empty-text="$t('No matching records found')"
          :sort-desc.sync="isSortDirDesc"
          class="position-relative"
        >
          <!-- Column: ID -->
          <template #cell(id)="data">
            <div style="margin-top: 0.3rem;">
              <b-list-group>
                <b-list-group-item
                  style="padding: 0.2rem 0.6rem 0.2rem 0.6rem;"
                >
                  <span style="margin-right: 0.6rem;">
                    <feather-icon
                      icon="HashIcon"
                      size="14"
                    />
                  </span>
                  <span style="font-size: 14px;">
                    <b-badge
                      pill
                      :variant="(skin === 'dark')? `light-info` : `info` "
                      class="text-capitalize"
                    >
                      {{ data.item.id }}
                    </b-badge>
                  </span>
                </b-list-group-item>
              </b-list-group>
            </div>
          </template>

          <!-- Column: Login Channel -->
          <template #cell(login)="data">
            <div style="margin-top: 0.3rem;">
              <b-list-group>
                <b-list-group-item
                  style="padding: 0.2rem 0.6rem 0.2rem 0.6rem;"
                >
                  <span style="font-size: 14px;">{{ $t('Login Channel ID') }}</span>
                  <span style="margin-left: 0.4rem;">
                    <feather-icon
                      icon="ChevronsRightIcon"
                      size="14"
                    />
                  </span>
                </b-list-group-item>
                <b-list-group-item
                  style="padding: 0.2rem 0.6rem 0.2rem 0.6rem;"
                >
                  <span style="font-size: 14px;">{{ `${data.item.login_channel_id}` }}</span>
                </b-list-group-item>
              </b-list-group>
            </div>
          </template>

          <!-- Column: Liff -->
          <template #cell(liff)="data">
            <div style="margin-top: 0.3rem;">
              <b-list-group>
                <b-list-group-item
                  style="padding: 0.2rem 0.6rem 0.2rem 0.6rem;"
                >
                  <span style="font-size: 14px;">{{ $t('Liff') }}</span>
                  <span style="margin-left: 0.4rem;">
                    <feather-icon
                      icon="ChevronsRightIcon"
                      size="14"
                    />
                  </span>
                </b-list-group-item>
                <b-list-group-item
                  style="padding: 0.2rem 0.6rem 0.2rem 0.6rem;"
                >
                  <span style="font-size: 14px;">{{ $t('App Name') }}</span>
                  <span style="margin-right: 0.6rem;">
                    <feather-icon
                      icon="ChevronRightIcon"
                      size="14"
                    />
                  </span>
                  <span style="font-size: 14px;">
                    {{ `${data.item.liff_app_name}` }}
                  </span>
                </b-list-group-item>
                <b-list-group-item
                  style="padding: 0.2rem 0.6rem 0.2rem 0.6rem;"
                >
                  <span style="font-size: 14px;">{{ $t('ID') }}</span>
                  <span style="margin-right: 0.6rem;">
                    <feather-icon
                      icon="ChevronRightIcon"
                      size="14"
                    />
                  </span>
                  <span style="font-size: 14px;">
                    {{ `${data.item.liff_id}` }}
                  </span>
                </b-list-group-item>
                <b-list-group-item
                  style="padding: 0.2rem 0.6rem 0.2rem 0.6rem;"
                >
                  <span style="font-size: 14px;">{{ $t('URL') }}</span>
                  <span style="margin-right: 0.6rem;">
                    <feather-icon
                      icon="ChevronRightIcon"
                      size="14"
                    />
                  </span>
                  <span style="font-size: 14px;">
                    <b-link
                      class="ml-25"
                      :href="data.item.liff_url"
                      target="_blank"
                    >
                      {{ `${data.item.liff_url}` }}
                    </b-link>
                  </span>
                </b-list-group-item>
                <b-list-group-item
                  style="padding: 0.2rem 0.6rem 0.2rem 0.6rem;"
                >
                  <span style="font-size: 14px;">{{ $t('Size') }}</span>
                  <span style="margin-right: 0.6rem;">
                    <feather-icon
                      icon="ChevronRightIcon"
                      size="14"
                    />
                  </span>
                  <span style="font-size: 14px;">
                    <b-badge
                      pill
                      :variant="(skin === 'dark')? `light-success` : `success` "
                    >
                      {{ `${$t(textFirstUpper(data.item.liff_size))}` }}
                    </b-badge>
                  </span>
                </b-list-group-item>
              </b-list-group>
            </div>
          </template>

          <!-- Column: Status -->
          <template #cell(status)="data">
            <b-badge
              pill
              :variant="`light-${resolveUserStatusVariant(data.item.status)}`"
              class="text-capitalize"
            >
              {{ $t(textFirstUpper(data.item.status)) }}
            </b-badge>
          </template>

          <!-- Column: Actions -->
          <template #cell(actions)="data">

            <div class="text-nowrap">

              <a
                class="action-icon"
                @click.stop="editLineLiffForm(data)"
              >
                <feather-icon
                  v-if="$can('edit', 'LineLiff')"
                  :id="`line-liff-row-${data.item.id}-edit-icon`"
                  icon="EditIcon"
                  variant="light-success"
                  size="16"
                  class="cursor-pointer mr-1"
                />
                <b-tooltip
                  v-if="$can('edit', 'LineLiff')"
                  :title="`${$t('Edit')} ${$t('LineLiff')}`"
                  :target="`line-liff-row-${data.item.id}-edit-icon`"
                  :delay="{ show: 3000, hide: 50 }"
                  placement="right"
                />
              </a>
              <a
                class="action-icon"
                @click.stop="deleteLineLiffForm(data)"
              >
                <feather-icon
                  v-if="$can('delete', 'LineLiff')"
                  :id="`line-liff-row-${data.item.id}-delete-icon`"
                  icon="TrashIcon"
                  variant="light-success"
                  size="16"
                  class="cursor-pointer mr-1"
                />
                <b-tooltip
                  v-if="$can('delete', 'LineLiff')"
                  :title="`${$t('Delete')} ${$t('LineLiff')}`"
                  :target="`line-liff-row-${data.item.id}-delete-icon`"
                  :delay="{ show: 3000, hide: 50 }"
                  placement="right"
                />
              </a>

            </div>
          </template>

        </b-table>
        <div class="mx-2 mb-2">
          <b-row>

            <!-- Per Page -->
            <b-col
              cols="12"
              sm="12"
              md="6"
            >
              <b-row>
                <b-col
                  cols="12"
                  sm="12"
                  md="3"
                  class="d-flex align-items-center justify-content-start mb-1 mb-md-0 pr-0"
                >
                  <label>{{ $t('Entries') }}</label>
                  <v-select
                    v-model="perPage"
                    :dir="$store.state.appConfig.isRTL ? 'rtl' : 'ltr'"
                    :options="perPageOptions"
                    :clearable="false"
                    class="per-page-selector d-inline-block ml-50 mr-1 w-100"
                  />
                </b-col>
                <b-col
                  cols="12"
                  sm="12"
                  md="9"
                  class="d-flex align-items-center justify-content-start mb-1 mb-md-0 pl-md-0 pr-md-0"
                >
                  <div class="d-flex align-items-center justify-content-end w-100">
                    <b-form-input
                      v-model="searchQuery"
                      class="d-inline-block mr-1"
                      :placeholder="`${$t('Search')}...`"
                    />
                    <b-button
                      v-if="$can('create', 'LineLiff')"
                      variant="primary"
                      style="width: 100%; max-width: 180px;"
                      @click="addLineLiffForm"
                    >
                      {{ `${$t('Add')} ${$t('LineLiff')}` }}
                    </b-button>
                  </div>
                </b-col>
              </b-row>
            </b-col>
            <!-- Pagination -->
            <b-col
              cols="12"
              sm="12"
              md="6"
              class="d-flex align-items-center justify-content-center justify-content-sm-end"
            >
              <span class="text-muted mr-2">
                {{ $t('Showing {from} to {to} of {of} entries',
                      {from: dataMeta.from, to: dataMeta.to, of: dataMeta.of })
                }}
              </span>
              <b-pagination
                v-model="currentPage"
                :total-rows="totals"
                :per-page="perPage"
                first-number
                last-number
                class="mb-0 mt-1 mt-sm-0"
                prev-class="prev-item"
                next-class="next-item"
              >
                <template #prev-text>
                  <feather-icon
                    icon="ChevronLeftIcon"
                    size="18"
                  />
                </template>
                <template #next-text>
                  <feather-icon
                    icon="ChevronRightIcon"
                    size="18"
                  />
                </template>
              </b-pagination>

            </b-col>

          </b-row>
        </div>
      </b-card>

      <line-liff-form
        v-model="shallShowLineLiffFormModal"
        :line-liff-data="lineLiffData"
        :type-line-liff-form="typeLineLiffForm"
        @update-line-liff-form="updateLineLiffForm"
        @discard-line-liff-form="discardLineLiffForm"
      />
    </b-overlay>
  </div>
</template>

<script>
import {
  BCard, BRow, BCol, BFormInput,
  BTable,
  BBadge, BPagination, BTooltip, BListGroup, BListGroupItem,
  BButton,
  BOverlay,
  BLink,
} from 'bootstrap-vue'
import vSelect from 'vue-select'
import {
  ref,
  onUnmounted,
} from '@vue/composition-api'
import store from '@/store'
import ToastificationContent from '@core/components/toastification/ToastificationContent.vue'
import FeatherIcon from '@/@core/components/feather-icon/FeatherIcon.vue'
import LineLiffTable from './LineLiffTable'
import LineLiffStore from './LineLiffStore'
import LineLiffForm from './LineLiffForm.vue'

export default {
  components: {
    LineLiffForm,

    BCard,
    BRow,
    BCol,
    BFormInput,
    BTable,
    BBadge,
    BPagination,
    BTooltip,
    BListGroup,
    BListGroupItem,
    BButton,
    BOverlay,
    BLink,

    vSelect,
    FeatherIcon,
  },
  data() {
    return {
      showOverlay: false,
      defaultData: {},
      lineLiffData: {},
      dialog: false,
      skin: store.state.appConfig.layout.skin,
      collapsedFilter: true,
      typeOptions: [],
    }
  },
  mounted() {
    this.locale = this.$i18n.locale
    this.getDefaultLineLiff()
  },
  methods: {
    getDefaultLineLiff() {
      this.showOverlay = true
      store
        .dispatch('line-liff-store/getDefaultLineLiff', { language: this.$i18n.locale })
        .then(response => {
          const okMsg = this.$i18n.t('Process on successfully')
          this.$toast({
            component: ToastificationContent,
            props: {
              title: okMsg,
              icon: 'CheckCircleIcon',
              variant: 'success',
            },
          },
          {
            position: 'bottom-right',
          })
          this.showOverlay = false
          this.defaultData = response.data
          this.lineLiffData = this.defaultData
        })
        .catch(error => {
          const { response, message } = error
          if (response) {
            const errorMsg = this.$i18n.t(response.data.error)
            this.$toast({
              component: ToastificationContent,
              props: {
                title: errorMsg,
                icon: 'AlertTriangleIcon',
                variant: 'danger',
              },
            },
            {
              position: 'bottom-right',
            })
          } else if (message) {
            this.$toast({
              component: ToastificationContent,
              props: {
                title: error.message,
                icon: 'AlertTriangleIcon',
                variant: 'danger',
              },
            },
            {
              position: 'bottom-right',
            })
          }
          this.showOverlay = false
        })
    },
    addLineLiffForm() {
      this.showOverlay = true
      store
        .dispatch('line-liff-store/getDefaultLineLiff', { language: this.$i18n.locale })
        .then(response => {
          const okMsg = this.$i18n.t('Process on successfully')
          this.$toast({
            component: ToastificationContent,
            props: {
              title: okMsg,
              icon: 'CheckCircleIcon',
              variant: 'success',
            },
          },
          {
            position: 'bottom-right',
          })
          this.showOverlay = false
          this.defaultData = response.data
          this.lineLiffData = response.data
          this.typeLineLiffForm = 'Add'
          this.shallShowLineLiffFormModal = true
        })
        .catch(error => {
          const { response, message } = error
          if (response) {
            const errorMsg = this.$i18n.t(response.data.error)
            this.$toast({
              component: ToastificationContent,
              props: {
                title: errorMsg,
                icon: 'AlertTriangleIcon',
                variant: 'danger',
              },
            },
            {
              position: 'bottom-right',
            })
          } else if (message) {
            this.$toast({
              component: ToastificationContent,
              props: {
                title: error.message,
                icon: 'AlertTriangleIcon',
                variant: 'danger',
              },
            },
            {
              position: 'bottom-right',
            })
          }
          this.showOverlay = false
        })
    },
    editLineLiffForm(data) {
      this.showOverlay = true
      store
        .dispatch('line-liff-store/getLineLiff', { language: this.$i18n.locale, id: data.item.id })
        .then(response => {
          const okMsg = this.$i18n.t('Process on successfully')
          this.$toast({
            component: ToastificationContent,
            props: {
              title: okMsg,
              icon: 'CheckCircleIcon',
              variant: 'success',
            },
          },
          {
            position: 'bottom-right',
          })
          this.showOverlay = false
          this.lineLiffData = response.data
          this.typeLineLiffForm = 'Edit'
          this.shallShowLineLiffFormModal = true
        })
        .catch(error => {
          const { response, message } = error
          if (response) {
            const errorMsg = this.$i18n.t(response.data.error)
            this.$toast({
              component: ToastificationContent,
              props: {
                title: errorMsg,
                icon: 'AlertTriangleIcon',
                variant: 'danger',
              },
            },
            {
              position: 'bottom-right',
            })
          } else if (message) {
            this.$toast({
              component: ToastificationContent,
              props: {
                title: error.message,
                icon: 'AlertTriangleIcon',
                variant: 'danger',
              },
            },
            {
              position: 'bottom-right',
            })
          }
          this.showOverlay = false
        })
    },
    deleteLineLiffForm(data) {
      this.$swal({
        title: this.$i18n.t('Are you sure?'),
        text: this.$i18n.t("You won't be able to revert this"),
        icon: 'warning',
        showCancelButton: true,
        confirmButtonText: this.$i18n.t('Yes, delete it'),
        cancelButtonText: this.$i18n.t('Cancel'),
        customClass: {
          confirmButton: 'btn btn-primary',
          cancelButton: 'btn btn-outline-danger ml-1',
        },
        buttonsStyling: false,
      }).then(result => {
        if (result.value === true) {
          store.dispatch('line-liff-store/deleteLineLiff', data.item.id)
            .then(() => {
              const okMsg = this.$i18n.t('Process on successfully')
              this.$toast({
                component: ToastificationContent,
                props: {
                  title: okMsg,
                  icon: 'CheckCircleIcon',
                  variant: 'success',
                },
              },
              {
                position: 'bottom-right',
              })
              this.$swal({
                icon: 'success',
                title: this.$i18n.t('Deleted'),
                text: this.$i18n.t('Your data has been deleted'),
                timer: 3000,
                customClass: {
                  confirmButton: 'btn btn-success',
                },
              })
              this.refetchData()
            })
            .catch(error => {
              const { response, message } = error
              if (response) {
                let errorMsg = this.$i18n.t(response.data.error)
                if (errorMsg === '') {
                  errorMsg = this.$i18n.t(response.data.message)
                }
                this.$toast({
                  component: ToastificationContent,
                  props: {
                    title: errorMsg,
                    icon: 'AlertTriangleIcon',
                    variant: 'danger',
                  },
                },
                {
                  position: 'bottom-right',
                })
              } else if (message) {
                this.$toast({
                  component: ToastificationContent,
                  props: {
                    title: error.message,
                    icon: 'AlertTriangleIcon',
                    variant: 'danger',
                  },
                },
                {
                  position: 'bottom-right',
                })
              }
            })
        }
      })
    },
    updateLineLiffForm() {
      this.refetchData()
    },
    discardLineLiffForm() {
      this.refetchData()
    },
    onCopy(data) {
      const copyText = data
      navigator.clipboard.writeText(copyText).then(() => {
        const okMsg = this.$i18n.t('Copied to clipboard')
        this.$toast({
          component: ToastificationContent,
          props: {
            title: okMsg,
            icon: 'CheckCircleIcon',
            variant: 'success',
          },
        },
        {
          position: 'top-center',
        })
      })
    },
  },
  setup() {
    const shallShowLineLiffFormModal = ref(false)
    const typeLineLiffForm = ref('')

    if (!store.hasModule('line-liff-store')) store.registerModule('line-liff-store', LineLiffStore)
    onUnmounted(() => {
      if (store.hasModule('line-liff-store')) store.unregisterModule('line-liff-store')
    })

    const {
      getList,
      tableColumns,
      perPage,
      currentPage,
      totals,
      dataMeta,
      perPageOptions,
      searchQuery,
      sortBy,
      isSortDirDesc,
      local,
      refTable,
      refetchData,
      resolveUserStatusVariant,
      textFirstUpper,
    } = LineLiffTable()

    return {
      shallShowLineLiffFormModal,
      typeLineLiffForm,

      getList,
      tableColumns,
      perPage,
      currentPage,
      totals,
      dataMeta,
      perPageOptions,
      searchQuery,
      sortBy,
      isSortDirDesc,
      local,
      refTable,
      refetchData,
      resolveUserStatusVariant,
      textFirstUpper,
    }
  },
}
</script>

<style lang="scss">
@import '@core/scss/vue/libs/vue-select.scss';
// @import "~@core/scss/base/lineapi/line-liff.scss";
</style>

<style lang="scss">
@import '~@core/scss/base/bootstrap-extended/include';
@import '~@core/scss/base/components/variables-dark';

.dark-layout {
  div{
    .b-overlay {
      .bg-light {
        background-color: $theme-dark-body-bg !important;
      }
    }
    .swal2-popup {
      background-color: $theme-dark-body-bg !important;
    }
  }
  .table.b-table > tbody .b-table-row-selected.table-active td {
    background-color: $theme-dark-table-active-bg;
  }
  .table.b-table.table-hover > tbody > tr.table-active:hover td,
  .table.b-table.table-hover > tbody > tr.table-active:hover th {
    background-image: linear-gradient($theme-dark-table-active-hover-bg, $theme-dark-table-active-hover-bg);
  }
}
.b-table-selectable{
  .feather{
    font-size: 1.3rem;
  }
}
.action-icon {
  :hover {
    color: #EB008B !important;
  }
}
</style>

<style lang="scss" scope>
@import '@core/scss/base/bootstrap-extended/include';
.media-heading {
  color: $body-color;
  margin-bottom: 0;
  line-height: 1.2;
}
</style>

<style lang="scss">
.jsoneditor-poweredBy {
  display: none !important;
}
.jsoneditor {
  border: thin solid #d8d6de !important;
}
.jsoneditor-menu{
  background-color: #aaaaaa !important;
  border-bottom: 1px solid #aaaaaa !important;
}
</style>
